// ─── PLAN d'une enveloppe : ce qui est PRÉVU, mois par mois ───────
// Le pendant du relevé de compte, côté estimation (chantier du 10/08) :
//   Budget  estime      ← cet écran
//   Comptes constate
//   Suivi   montre le delta
// On n'y voit donc AUCUN montant réel : le réel se lit dans Comptes, l'écart
// dans Suivi. Mélanger les trois est exactement ce qui rendait l'ancien écran
// Budget illisible — il affichait des dépenses passées sous un nom qui
// promettait une prévision.
//
// Même squelette que le relevé : en-tête FIXE (total, recherche, mois), seule
// la liste défile.
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran,
  useUI, useT, confirmerSuppression,
} from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import {
  moisCourant, decalerMois, occurrencesDuMois, lignesDeBudget, nonDuesDuMois,
} from './services/occurrences';
import { filtrerPlan, TRIS_LIGNES, ETATS_PLAN, nbFiltresReleve } from './services/filtres';
import { emojiBudget, emojiCategorie, cleCategorie } from './data/categories';
import { statutDe } from './data/statuts';
import FeuilleFiltresCompte from './FeuilleFiltresCompte';
import BarreRecherche from './BarreRecherche';
import { prefSession, definirPrefSession } from './services/prefsSession';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const libelleMois = (mk, t) => {
  const [y, m] = String(mk).split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk} ${y}`;
};

// Ce qu'une ANNOTATION manuelle a changé sur ce mois. Un ⚠️ y figurait pour
// tout et n'importe quoi : un mois CLOS par l'utilisateur s'affichait donc
// avec l'icône de l'alerte (Cédric, 13/08 : « à quoi sert le triangle ? »).
// On reprend le vocabulaire commun — le même cadenas, la même cloche barrée —
// et ✏️ pour un montant ajusté, qui n'est pas un statut mais se voit aussi.
const marqueurException = (o) => {
  if (!o?.exception) return '';
  const bouts = [];
  if (o.exception.statut) {
    const st = statutDe(o);
    bouts.push(st.emoji);
    // Le cadenas accompagne le ✅ comme dans la fiche de prévision : c'est
    // le même état, il doit se reconnaître d'un écran à l'autre (Cédric,
    // 13/08). `data/statuts.js` porte déjà l'information.
    // Plus de 🔒 : le ✅ du statut « validé » se suffit (15/08).
  }
  if (o.exception.montantAjuste != null) bouts.push('✏️');
  return bouts.length ? `  ·  ${bouts.join(' ')}` : '';
};

export default function EcranEnveloppe({
  budget, lignes = [], exceptions = [], actuals = [], rattachements = [],
  onFermer, onNouvelle, onFiche, onFicheLigne, onEditerLigne, onSupprimerLigne, onOperations,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [mois, setMois] = useState(moisCourant);
  const [recherche, setRecherche] = useState('');
  const [sens, setSens] = useState('tout');
  const [tri, setTri] = useState('montant');
  const [etat, setEtat] = useState('tout');   // rattachement : tout | libre | partielle | rattachee
  const [feuille, setFeuille] = useState(null);
  // La SYNTHÈSE se plie depuis l'en-tête (16/08) — mémorisé pour la session.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseEnveloppe', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseEnveloppe', !v);
    return !v;
  });

  const mesLignes = useMemo(() => lignesDeBudget(lignes, budget?.id), [lignes, budget]);
  // Les occurrences sont calculées AVEC le réel (11/08). La LISTE, elle,
  // continue de n'afficher que le prévu — c'est le rôle de cet écran. Mais
  // l'objet occurrence sert aussi à la fiche, qui affiche « PRÉVU / RÉEL /
  // ÉCART » : lui passer des listes vides ne la rendait pas discrète, ça lui
  // faisait afficher un réel de 0 alors qu'un rattachement existait. Une
  // affirmation fausse, pas une abstention.
  const occurrences = useMemo(
    () => occurrencesDuMois({ lignes: mesLignes, actuals, exceptions, rattachements, mk: mois }),
    [mesLignes, actuals, exceptions, rattachements, mois],
  );
  const liste = useMemo(() => filtrerPlan(occurrences, { recherche, sens, etat, tri }), [occurrences, recherche, sens, etat, tri]);
  // Ce que le mois ÉCARTE, et pourquoi. Sans cette liste, une ligne mal
  // configurée (début en septembre, trimestrielle, terminée) disparaissait
  // sans laisser de trace : impossible de distinguer « pas due ce mois-ci »
  // de « je l'ai mal saisie » ou de « elle n'existe pas ».
  const nonDues = useMemo(
    () => nonDuesDuMois({ lignes: mesLignes, exceptions, mk: mois }),
    [mesLignes, exceptions, mois],
  );
  // Total du PÉRIMÈTRE filtré, insensible à la recherche : un total qui change
  // à chaque lettre tapée n'aurait aucun sens.
  const totalPrevu = useMemo(
    () => filtrerPlan(occurrences, { sens }).reduce((n, o) => n + o.prevuSigne, 0),
    [occurrences, sens],
  );
  // Les deux flux à droite du net, comme sur l'écran de tous les budgets
  // (Cédric, 15/08) : le net seul cache l'ampleur de chacun. MÊME périmètre
  // que le total — le filtre s'applique, la recherche non — et mêmes
  // exclusions que la carte racine : ignorées et absentes.
  const fluxPrevus = useMemo(() => {
    let debit = 0, credit = 0;
    for (const o of filtrerPlan(occurrences, { sens })) {
      if (o.statut === 'ignoree' || o.statut === 'absente') continue;
      if (o.ligne?.sens === 'revenu') credit += Math.abs(o.prevu || 0);
      else debit += Math.abs(o.prevu || 0);
    }
    return { debit, credit };
  }, [occurrences, sens]);

  const surCourant = mois === moisCourant();
  const nbFiltres = nbFiltresReleve({ sens }) + (etat !== 'tout' ? 1 : 0);

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={budget?.nom || ''} iconeTitre={emojiBudget(budget)} onRetour={onFermer}
          action={{ icone: '＋', label: t('finances.nouvelleLigne'), onPress: onNouvelle }}>
          {/* Les opérations RÉELLES de l'enveloppe et « qui doit à qui » ne
              disparaissent pas : ils gardent leur écran, accessible ici.
              Cédric (10/08) : un budget peut être partagé, sa relation au
              compte se définira plus tard. */}
          <TouchableOpacity onPress={onOperations} hitSlop={HIT} accessibilityLabel={t('finances.operationsPonct')}>
            <Text style={{ fontSize: 19, marginLeft: 14 }}>🧾</Text>
          </TouchableOpacity>
          {/* La bascule de SYNTHÈSE (16/08) : grisée quand la carte est cachée. */}
          <TouchableOpacity onPress={basculerSynthese} hitSlop={HIT}
            accessibilityLabel={t('finances.basculerSynthese')}>
            <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
          </TouchableOpacity>
        </PageHeader>

        <View style={s.entete}>
          {syntheseVisible && (
          <View style={[s.totalCarte, { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.totalLbl}>{t('finances.suiviPrevu')}  ·  {libelleMois(mois, t)}</Text>
              <Text style={[s.totalVal, totalPrevu < 0 && { color: UI.danger || '#c0392b' }]}>
                {formatEuro(totalPrevu)}
              </Text>
              <Text style={s.detail}>{liste.length} {t('finances.mouvements')}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={[s.fluxVal, { color: UI.ok || '#2e7d32' }]}>
                + {formatEuro(fluxPrevus.credit)}
              </Text>
              <Text style={[s.fluxVal, { color: UI.danger || '#c0392b' }]}>
                − {formatEuro(fluxPrevus.debit)}
              </Text>
            </View>
          </View>
          )}

          {/* Le modèle commun : 🔽 filtre · champ · ↕ tri (15/08). */}
          <BarreRecherche valeur={recherche} onChange={setRecherche}
            nbFiltres={nbFiltres} onFiltres={() => setFeuille('filtres')}
            triActif={tri !== 'montant'} onTri={() => setFeuille('tri')} />

          <View style={s.barre}>
            <TouchableOpacity onPress={() => setMois(decalerMois(mois, -1))} hitSlop={HIT}>
              <Text style={s.nav}>‹</Text>
            </TouchableOpacity>
            <View style={s.titreZone}>
              <Text style={s.mois}>{libelleMois(mois, t)}</Text>
              <TouchableOpacity onPress={() => setMois(moisCourant())} hitSlop={HIT} disabled={surCourant}>
                <Text style={[s.cible, surCourant && s.navOff]}>🎯</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={() => setMois(decalerMois(mois, 1))} hitSlop={HIT}>
              <Text style={s.nav}>›</Text>
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={s.corps}>
          {liste.length === 0 ? (
            <EmptyState icon="📋" texte={t('finances.aucuneOpPeriode', { periode: libelleMois(mois, t) })}
              aide={t('finances.aucuneLigne')} />
          ) : liste.map(o => (
            <LigneSwipe key={o.ligneId} espacement={8} enabled
              onEditer={() => onEditerLigne?.(o.ligne)}
              onSupprimer={() => confirmerSuppression(
                t('finances.actSupprimerLigne'), o.ligne?.libelle || '',
                () => onSupprimerLigne?.(o.ligne))}>
              {/* Tap = fiche en lecture (principe app-wide) ; l'édition est au
                  swipe →. C'est la MÊME fiche d'occurrence que dans Suivi :
                  deux fiches pour un même objet divergeraient tôt ou tard. */}
              <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => onFiche?.(o)}>
                <Text style={{ fontSize: 19 }}>{emojiCategorie(o.ligne?.categorie)}</Text>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.libelle} numberOfLines={1}>
                    {o.ligne?.libelle || t(cleCategorie(o.ligne?.categorie))}
                  </Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {t(`finances.${o.ligne?.frequence === 'ponctuelle' ? 'ponctuelle' : 'recurrente'}`)}
                    {marqueurException(o)}
                  </Text>
                </View>
                <Text style={[s.montant, o.ligne?.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
                  {montantOperation(o.prevu, o.ligne?.sens)}
                </Text>
              </TouchableOpacity>
            </LigneSwipe>
          ))}

          {/* Ce que ce mois n'applique PAS, avec le motif de chacune. Affiché
              en retrait : ce n'est pas le plan du mois, c'est ce qui explique
              son absence. */}
          {nonDues.length > 0 && (
            <>
              <Text style={s.titreSection}>{t('finances.nonApplicables')}  ·  {nonDues.length}</Text>
              <Text style={s.aideSection}>{t('finances.nonApplicablesAide')}</Text>
              {/* Pas d'occurrence pour une ligne non due : le tap ouvre la
                  fiche de la PRÉVISION, celle qui montre pourquoi elle ne
                  tombe pas ce mois-ci. */}
              {nonDues.map(nd => (
                /* Elles aussi se gèrent au swipe (audit du 15/08) : une ligne
                   mal configurée se corrige d'ici, sans passer par la fiche. */
                <LigneSwipe key={nd.ligneId} espacement={8} enabled
                  onEditer={() => onEditerLigne?.(nd.ligne)}
                  onSupprimer={() => confirmerSuppression(
                    t('finances.actSupprimerLigne'), nd.ligne?.libelle || '',
                    () => onSupprimerLigne?.(nd.ligne))}>
                <TouchableOpacity style={[s.carte, s.carteOff]} activeOpacity={0.7}
                  onPress={() => onFicheLigne?.(nd.ligne)}>
                  <Text style={{ fontSize: 19, opacity: 0.5 }}>{emojiCategorie(nd.ligne?.categorie)}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={[s.libelle, s.txtOff]} numberOfLines={1}>
                      {nd.ligne?.libelle || t(cleCategorie(nd.ligne?.categorie))}
                    </Text>
                    <Text style={s.sous} numberOfLines={1}>
                      {t(`finances.nd_${nd.motif}`, { mois: libelleMois(nd.mois, t) })}
                    </Text>
                  </View>
                  <Text style={[s.montant, s.txtOff]}>
                    {montantOperation(nd.ligne?.montant, nd.ligne?.sens)}
                  </Text>
                </TouchableOpacity>
                </LigneSwipe>
              ))}
            </>
          )}
        </ScrollView>

        <FeuilleFiltresCompte visible={!!feuille} ongletInitial={feuille || 'filtres'}
          sens={sens} tri={tri} tris={TRIS_LIGNES} avecHorsCalculVisible={false}
          etat={etat} etats={ETATS_PLAN}
          onAppliquer={({ sens: se, tri: x, etat: e }) => { setSens(se); setTri(x); setEtat(e); }}
          onFermer={() => setFeuille(null)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  entete: { paddingHorizontal: 12, paddingTop: 12, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  corps: { padding: 12, paddingBottom: 30 },
  barre: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 8 },
  titreZone: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  mois: { fontSize: 16, fontWeight: '800', color: UI.ink },
  cible: { fontSize: 15 },
  nav: { fontSize: 24, color: UI.accent, paddingHorizontal: 4 },
  navOff: { opacity: 0.25 },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 10 },
  totalLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  totalVal: { fontSize: 21, fontWeight: '800', color: UI.ink, marginTop: 2 },
  detail: { fontSize: 12, color: UI.muted, marginTop: 5, fontWeight: '700' },
  fluxVal: { fontSize: 14.5, fontWeight: '800', marginTop: 1 },
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  libelle: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  montant: { fontSize: 15, fontWeight: '800', color: UI.ink },
  // La section « non applicables » est en RETRAIT, pas masquee : elle
  // explique une absence, elle ne fait pas partie du plan du mois.
  carteOff: { opacity: 0.6, borderStyle: 'dashed' },
  txtOff: { color: UI.muted },
  titreSection: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 18, marginBottom: 4 },
  aideSection: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginBottom: 8 },
});
